import { IGetRewards, IReward, IRewardMap } from "../interfaces";
import { post } from "../util/api";
import { sleep } from "../util/sleep";

/* PARAM INTERFACE */
export interface IConsumeRewardParams {
  timeLeft?: number;
  selectedSlotIndex?: number;
  score?: number;
  generateNewReward?: boolean;
  createNewReward?: boolean;
  rewardUserId?: string;
  refreshReward?: boolean;
}

/* HELPER #1 */
export const fetchAllRewards = async (
  token: string,
  campaignId?: string,
  rewardUserId?: string
): Promise<IRewardMap> => {
  let url = `${process.env.HOST_ENV}/reward/v1.1/user/reward`;
  if (campaignId) {
    url = url + "?campaignId=" + campaignId;
  } else if (rewardUserId) {
    url = url + "?rewardUserId=" + rewardUserId;
  }
  try {
    let rewards: IRewardMap = null;
    if (process.env.MODE === "DEMO") {
      const urlParams = new URLSearchParams(window.location.href);
      const configParam = urlParams.get("config");
      if(configParam){
        rewards = {allIds:[],byId:{}};
      }else{
      rewards = await (await import("./mock/reward")).default();
      }
    } else {
      const res = await post<IGetRewards>(url, {}, token);
      const body = res.parsedBody;
      if (!body || !body.success || !body.data) {
        throw `Invalid Response, ${
          body && body["message"] ? "cause: " + body["message"] : null
        }`;
      }
      rewards = body.data.rewards;
    }

    const currentDate = Math.floor(new Date().valueOf() / 1000);

    rewards.allIds = rewards.allIds.filter((id) => {
      const r = rewards.byId[id];
      if (r["fragmentMap"]) {
        r.layout = {
          campaignId: r.campaignId,
          layout: {
            ...r["fragmentMap"]["fragments"]["reward"],
            data: r["fragmentMap"]["data"],
          },
          programLayout: null,
          reportingVersion: r["fragmentMap"]["reportingVersion"],
          analyticsMeta: {
            "campaignName":  r["fragmentMap"]["analyticsMeta"] && r["fragmentMap"]["analyticsMeta"] && r["fragmentMap"]["analyticsMeta"]["campaignName"]
          },
        };
      }
      if (!r.layout?.layout) return false;
      if (r.experienceConsumed && r.slots) {
        const slotReward = r.slots[r.selectedSlotIndex].reward;
        if (slotReward.rewardProperties.couponExpiry?.expiry) {
          if (slotReward.rewardProperties.couponExpiry.expiry > currentDate)
            return true;
          else return false;
        }
        return true;
      } else {
        if (r.layout?.layout?.hidden) return false;
        else if (r.rewardExpiry?.expiry) {
          if (r.rewardExpiry.expiry > currentDate) return true;
          else return false;
        } else {
          return true;
        }
      }
    });
    return rewards;
  } catch (e) {
    throw e;
  }
};

/* HELPER #2 */
export const redeemReward = async (
  reward: IReward,
  params: IConsumeRewardParams,
  token: string
): Promise<IReward> => {
  if (process.env.MODE === "DEMO" || window["skipSeen"]) {
    return await sleep({
      ...reward,
      seenStatus: "CONSUME_SUCCESS",
      status: "redeemable-seen",
    });
  } else {
    const url = `${process.env.HOST_ENV}/reward/v1.1/user/reward/seen`;
    try {
      const res = await post<{ reward: IReward; success: boolean }>(
        url,
        { rewardUserId: reward.rewardUserId, ...params },
        token
      );
      const body = res.parsedBody;

      if (!body || !body.success || !body.reward) {
        throw new Error("Error redeeming reward");
      }
      const seenReward = body.reward;
      const newReward: IReward = {
        ...reward,
        experienceConsumed: seenReward.experienceConsumed,
        rewardProperties: seenReward.rewardProperties,
        seenStatus: "CONSUME_SUCCESS",
        status: "redeemable-seen",
      };
      return newReward;
    } catch (e) {
      throw e;
    }
  }
};
